import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import * as Sentry from '@sentry/react';
import { isIOS } from '@/constants';
import { VIDEO_BITS_PER_SECOND } from './constants';

interface VideoRecordProps {
  mediaRecordRef: React.MutableRefObject<MediaRecorder | null>;
  setIsRecordReady: (ready: boolean) => void;
  setVideoRecordDataWebm: (blob?: Blob) => void;
  cameraWidth: number;
  setRecordReplaySrc: (src: string | null) => void;
  /** Called with (replaySrc, blob) when a recording is ready */
  onRecordReady?: (replaySrc: string, blob: Blob) => void;
}

const MIME_TYPES = [
  'video/mp4',
  'video/webm;codecs="vp8,opus"',
  'video/webm;codecs=h264',
  'video/webm;codecs=vp9',
  'video/webm',
];

const videoConstraints = {
  frameRate: 30,
  facingMode: 'user',
};

function getSupportedMimeType(): string | undefined {
  if (!window.MediaRecorder?.isTypeSupported) return undefined;
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

const VideoRecord = forwardRef<HTMLVideoElement, VideoRecordProps>(
  (
    {
      mediaRecordRef,
      setIsRecordReady,
      setVideoRecordDataWebm,
      cameraWidth,
      setRecordReplaySrc,
      onRecordReady,
    },
    ref,
  ) => {
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const videoElRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const setVideoEl = (el: HTMLVideoElement | null) => {
      videoElRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    const turnOffCamera = useCallback(() => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false;
        });
        streamRef.current = null;
      }

      if (videoElRef.current) {
        videoElRef.current.srcObject = null;
      }

      if (mediaRecordRef.current) {
        // Detach handlers before stopping so teardown doesn't assemble a stray
        // replay blob from the in-flight chunks.
        mediaRecordRef.current.ondataavailable = null;
        mediaRecordRef.current.onstop = null;
        try {
          mediaRecordRef.current.stop();
        } catch {
          // already stopped
        }
        mediaRecordRef.current = null;
      }

      setIsCameraOn(false);
    }, [mediaRecordRef]);

    const setupMediaRecorder = useCallback(
      (stream: MediaStream) => {
        const mimeType = getSupportedMimeType();

        try {
          const options: MediaRecorderOptions = {
            videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
          };
          if (mimeType) options.mimeType = mimeType;
          mediaRecordRef.current = new MediaRecorder(stream, options);
        } catch (e) {
          console.warn('MediaRecorder with options failed, using defaults:', e);
          mediaRecordRef.current = new MediaRecorder(stream);
        }

        const recorder = mediaRecordRef.current;

        // Recording is started with a timeslice (see RECORDER_TIMESLICE_MS), so
        // ondataavailable fires repeatedly during capture with partial slices.
        // Accumulate them and assemble a single blob on stop — overwriting on
        // each slice would keep only the last second. The timeslice is what
        // keeps iOS from freezing the video track on long recordings.
        let chunks: Blob[] = [];

        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = () => {
          if (chunks.length === 0) return;

          // Use the recorder's actual negotiated type (mp4 on iOS, webm on
          // Chrome/Android) so the replay <video> gets correctly-labelled data
          // instead of a hardcoded guess.
          const actualType = recorder.mimeType || mimeType || 'video/mp4';
          const blob = new Blob(chunks, { type: actualType });
          chunks = [];

          setIsRecordReady(true);
          setVideoRecordDataWebm(blob);

          const url = URL.createObjectURL(blob);
          setRecordReplaySrc(url);
          onRecordReady?.(url, blob);
        };
      },
      [
        mediaRecordRef,
        onRecordReady,
        setIsRecordReady,
        setRecordReplaySrc,
        setVideoRecordDataWebm,
      ],
    );

    const handleSuccess = useCallback(
      (stream: MediaStream) => {
        if (videoElRef.current) {
          videoElRef.current.srcObject = stream;
          // iOS requires explicit play() even with autoPlay attribute
          videoElRef.current.play().catch(() => {});
        }

        streamRef.current = stream;
        setupMediaRecorder(stream);
        setIsCameraOn(true);
        setIsConnecting(false);
      },
      [setupMediaRecorder],
    );

    const detectDeniedPermissions = useCallback(async (): Promise<string[]> => {
      const denied: string[] = [];

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        denied.push('Camera');
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        denied.push('Microphone');
      }

      return denied;
    }, []);

    const handleError = useCallback(
      async (e: unknown) => {
        console.error('Failed to access camera:', e);

        if (e instanceof DOMException && e.name === 'NotAllowedError') {
          const denied = await detectDeniedPermissions();
          const permList = denied.length > 0 ? denied.join(' and ') : 'Camera and Microphone';

          Sentry.withScope((scope) => {
            scope.setTag('error.type', 'permission_denied');
            scope.setTag('platform', isIOS ? 'ios' : 'other');
            scope.setContext('permissions', {
              deniedPermissions: denied,
              userAgent: navigator.userAgent,
            });
            Sentry.captureMessage(
              `${permList} permission denied`,
              'warning',
            );
          });

          const instructions = isIOS
            ? `${permList} access is blocked. To fix this:\n\n` +
              '• Safari: Go to Settings → Safari → Camera & Microphone → set to "Allow"\n' +
              '• Chrome: Go to Settings → Chrome → Camera & Microphone → enable both\n\n' +
              'Then refresh this page.'
            : `${permList} access is blocked. Please click the lock/camera icon in the address bar to allow access, then refresh.`;

          setError(instructions);
        } else {
          Sentry.withScope((scope) => {
            scope.setTag('error.type', 'camera_unavailable');
            scope.setTag('platform', isIOS ? 'ios' : 'other');
            scope.setContext('camera', {
              errorName: e instanceof DOMException ? e.name : 'unknown',
              errorMessage: e instanceof Error ? e.message : String(e),
              userAgent: navigator.userAgent,
            });
            Sentry.captureException(e instanceof Error ? e : new Error(String(e)));
          });

          setError(
            'Video camera is not available. Please provide permissions and refresh the page.',
          );
        }
        setIsConnecting(false);
      },
      [detectDeniedPermissions],
    );

    const turnOnCamera = useCallback(() => {
      if (isCameraOn || isConnecting) return;

      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          'Camera is not supported in this browser context. Please make sure you are using HTTPS.',
        );
        return;
      }

      setIsConnecting(true);
      setError(null);

      // Call getUserMedia synchronously from the click handler —
      // do NOT await anything before this call, or iOS will consider
      // the user activation expired and deny permission.
      const constraints: MediaStreamConstraints = {
        video: { frameRate: videoConstraints.frameRate, facingMode: videoConstraints.facingMode },
        audio: true,
      };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(handleSuccess)
        .catch((err) => {
          // Retry with basic constraints on device-specific failures
          if (
            err instanceof DOMException &&
            (err.name === 'NotReadableError' || err.name === 'OverconstrainedError')
          ) {
            console.warn(`Got ${err.name}, retrying with fallback constraints`);
            return navigator.mediaDevices
              .getUserMedia({ video: true, audio: true })
              .then(handleSuccess);
          }
          throw err;
        })
        .catch(handleError);
    }, [isCameraOn, isConnecting, cameraWidth, handleSuccess, handleError]);

    // Auto-init on non-iOS; iOS requires user gesture
    const autoInitAttempted = useRef(false);
    useEffect(() => {
      if (videoElRef.current && !isCameraOn && !autoInitAttempted.current) {
        autoInitAttempted.current = true;
        turnOnCamera();
      }
    }, [isCameraOn, turnOnCamera]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        turnOffCamera();
      };
    }, [turnOffCamera]);

    return (
      <>
        <div className="relative w-full">
          <video
            id="video-preview"
            playsInline
            muted
            autoPlay
            className="relative z-5 w-full"
            ref={setVideoEl}
          />
        </div>

        {!isCameraOn && (
          <button
            type="button"
            onClick={turnOnCamera}
            className="absolute inset-0 z-20 flex w-full items-center justify-center bg-black/70 text-white cursor-pointer border-none outline-none p-6"
          >
            {isConnecting ? (
              <span className="text-lg">Connecting camera...</span>
            ) : error ? (
              <span className="text-sm whitespace-pre-line text-left leading-relaxed">
                {error}
              </span>
            ) : (
              <span className="text-lg">Tap to enable camera</span>
            )}
          </button>
        )}
      </>
    );
  },
);

export default VideoRecord;
